import {
  MEMBER_PLAN_NAMES,
  RENTAL_STATUS_OPTIONS,
  type MemberEnvironment,
  type TicketHolding,
} from '@cortex/domain-ac-mens-hub-api';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/** @graph-connects none */
const STATUS_OPTIONS: { value: string; labelKey?: string }[] = [
  { value: '', labelKey: 'common.all' },
  { value: 'active' },
  { value: 'waiting' },
  { value: 'cancel_requested' },
  { value: 'canceled' },
];

/** @graph-connects none */
const inputBase =
  'rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2.5 py-1.5 text-[13px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-primary)]';

/** @graph-connects none */
const selectBase =
  'h-[33px] rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2.5 text-[13px] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-primary)]';

/** @graph-connects none */
const labelBase = 'text-[11px] font-medium text-[var(--color-text-secondary)]';

/**
 * 会員一覧の検索条件。空の項目は「絞り込まない」を意味する。
 *
 * @graph-connects none
 */
export interface MemberListFilterValues {
  arthurUserId?: number;
  lastName?: string;
  firstName?: string;
  email?: string;
  tel?: string;
  subscriptionStatus?: string;
  rentalStatuses?: readonly string[];
  planName?: string;
  ticketHolding?: TicketHolding;
}

/**
 * 会員一覧の検索フォーム。入力中の値はこのコンポーネントが持ち、
 * 「検索」を押した時点の条件だけを親へ渡す（打鍵ごとに再取得しない）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員一覧の検索フォーム。ID・氏名・連絡先・契約ステータスに加え、レンタルステータス(複数選択)・プラン・チケット保有で絞り込む条件を組み立てる。
 * @graph-connects none
 */
export function MemberListFilters({
  environment = 'production',
  onApply,
  onClear,
}: {
  /** STG では氏名・電話を表示しないため、その入力欄も出さない */
  environment?: MemberEnvironment;
  onApply: (values: MemberListFilterValues) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  // 見せない項目で検索できると、氏名を当てにいく使い方ができてしまう。
  // API 側でも同じ条件を落としているので、ここは操作を迷わせないための一次防御。
  const canSearchByName = environment !== 'staging';
  const [arthurUserId, setArthurUserId] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [tel, setTel] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [planName, setPlanName] = useState('');
  const [ticketHolding, setTicketHolding] = useState('');
  const [rentalStatuses, setRentalStatuses] = useState<readonly string[]>([]);

  /** @graph-connects none */
  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    onApply({
      arthurUserId: arthurUserId ? Number(arthurUserId) : undefined,
      lastName: canSearchByName ? lastName || undefined : undefined,
      firstName: canSearchByName ? firstName || undefined : undefined,
      email: email || undefined,
      tel: canSearchByName ? tel || undefined : undefined,
      subscriptionStatus: subscriptionStatus || undefined,
      // 何も選ばれていなければ絞り込まない。空配列を送ると API 側で弾かれる
      rentalStatuses: rentalStatuses.length > 0 ? rentalStatuses : undefined,
      planName: planName || undefined,
      ticketHolding: ticketHolding === '' ? undefined : (ticketHolding as TicketHolding),
    });
  }

  /** @graph-connects none */
  function handleClear() {
    setArthurUserId('');
    setLastName('');
    setFirstName('');
    setEmail('');
    setTel('');
    setSubscriptionStatus('');
    setPlanName('');
    setTicketHolding('');
    setRentalStatuses([]);
    onClear();
  }

  /** @graph-connects none */
  function toggleRentalStatus(status: string) {
    setRentalStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4"
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className={labelBase} htmlFor="member-search-id">
            arthur_user_id
          </label>
          <input
            id="member-search-id"
            type="text"
            placeholder={t('memberList.exactMatch')}
            value={arthurUserId}
            onChange={(e) => {
              setArthurUserId(e.target.value);
            }}
            className={`w-[140px] ${inputBase}`}
          />
        </div>
        {canSearchByName && (
          <div className="flex flex-col gap-1">
            <label className={labelBase} htmlFor="member-search-last-name">
              {t('memberList.lastName')}
            </label>
            <input
              id="member-search-last-name"
              type="text"
              placeholder={t('memberList.fuzzySearch')}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
              className={`w-[140px] ${inputBase}`}
            />
          </div>
        )}
        {canSearchByName && (
          <div className="flex flex-col gap-1">
            <label className={labelBase} htmlFor="member-search-first-name">
              {t('memberList.firstName')}
            </label>
            <input
              id="member-search-first-name"
              type="text"
              placeholder={t('memberList.fuzzySearch')}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
              className={`w-[140px] ${inputBase}`}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className={labelBase} htmlFor="member-search-email">
            {t('memberList.email')}
          </label>
          <input
            id="member-search-email"
            type="text"
            placeholder={t('memberList.fuzzySearch')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            className={`w-[220px] ${inputBase}`}
          />
        </div>
        {canSearchByName && (
          <div className="flex flex-col gap-1">
            <label className={labelBase} htmlFor="member-search-tel">
              {t('memberList.tel')}
            </label>
            <input
              id="member-search-tel"
              type="text"
              placeholder={t('memberList.exactMatch')}
              value={tel}
              onChange={(e) => {
                setTel(e.target.value);
              }}
              className={`w-[160px] ${inputBase}`}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className={labelBase} htmlFor="member-search-status">
            {t('memberList.status')}
          </label>
          <select
            id="member-search-status"
            value={subscriptionStatus}
            onChange={(e) => {
              setSubscriptionStatus(e.target.value);
            }}
            className={`w-[160px] ${selectBase}`}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.labelKey === undefined ? opt.value : t(opt.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelBase} htmlFor="member-search-plan">
            {t('memberList.plan')}
          </label>
          <select
            id="member-search-plan"
            value={planName}
            onChange={(e) => {
              setPlanName(e.target.value);
            }}
            className={`w-[210px] ${selectBase}`}
          >
            <option value="">{t('common.all')}</option>
            {MEMBER_PLAN_NAMES.map((plan) => (
              <option key={plan} value={plan}>
                {plan}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelBase} htmlFor="member-search-ticket">
            {t('memberList.ticketHolding')}
          </label>
          <select
            id="member-search-ticket"
            value={ticketHolding}
            onChange={(e) => {
              setTicketHolding(e.target.value);
            }}
            className={`w-[140px] ${selectBase}`}
          >
            <option value="">{t('common.all')}</option>
            <option value="none">{t('memberList.ticketHoldingNone')}</option>
            <option value="some">{t('memberList.ticketHoldingSome')}</option>
          </select>
        </div>
      </div>

      <div className="my-4 h-px bg-[var(--color-border)]" />

      <div>
        <div className="mb-2 flex flex-wrap items-baseline gap-2.5">
          <span className={labelBase}>{t('memberList.rentalStatus')}</span>
          <span className="text-[11px] text-[var(--color-text-disabled)]">
            {t('memberList.rentalStatusHint')}
          </span>
          {rentalStatuses.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setRentalStatuses([]);
              }}
              className="text-[11px] text-[var(--color-accent-primary)] underline"
            >
              {t('memberList.clearRentalStatus')}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {RENTAL_STATUS_OPTIONS.map((status) => {
            const checked = rentalStatuses.includes(status);
            return (
              <label
                key={status}
                className={`inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] ${
                  checked
                    ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]'
                    : 'border-[var(--color-border)] text-[var(--color-text-secondary)]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    toggleRentalStatus(status);
                  }}
                  className="h-3 w-3 accent-[var(--color-accent-primary)]"
                />
                {status}
              </label>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          className="h-[33px] rounded bg-[var(--color-accent-primary)] px-5 text-[13px] font-medium text-white"
        >
          {t('common.search')}
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="h-[33px] rounded border border-[var(--color-border)] px-4 text-[13px] text-[var(--color-text-secondary)]"
        >
          {t('common.clear')}
        </button>
      </div>
    </form>
  );
}
